import { useState, useCallback } from 'react';
import { useTinyUrl } from './useTinyUrl';
import { ExperienceType } from '@/components/experience-detail/types';
import { resolveFrontendBaseUrl, toAbsoluteUrl } from '@/utils/shareMetadata';

export interface UseShareOptions {
  type?: ExperienceType;
  slug?: string;
  customUrl?: string;
  /** When true, use current window URL (e.g. including ?booking=) in share popup and copy. */
  useFullUrl?: boolean;
  title?: string;
  description?: string;
}

export interface UseShareReturn {
  openSharePopup: (customUrl?: string) => Promise<void>;
  shareUrl: string;
  popupOpen: boolean;
  setPopupOpen: (open: boolean) => void;
  shareTitle: string;
  shareDescription: string;
  loading: boolean;
}

const getShareTitle = (type?: ExperienceType): string => {
  switch (type) {
    case 'package':
      return 'Share Package';
    case 'activity':
      return 'Share Activity';
    case 'stay':
      return 'Share Nexgo Space';
    case 'riders-corner':
      return "Share Rider's Corner";
    default:
      return 'Share';
  }
};

const getShareDescription = (type?: ExperienceType): string => {
  return '';
  switch (type) {
    case 'package':
      return 'Copy the link below to share this package with others.';
    case 'activity':
      return 'Copy the link below to share this activity with others.';
    case 'stay':
      return 'Copy the link below to share this Nexgo Space with others.';
    case 'riders-corner':
      return "Copy the link below to share this rider's corner with others.";
    default:
      return 'Copy the link below to share this with others.';
  }
};

const constructShareUrl = (
  type?: ExperienceType,
  slug?: string,
  customUrl?: string
): string => {
  // Guard against SSR
  if (typeof window === 'undefined') return '';

  // Use custom URL if provided
  if (customUrl) {
    return toAbsoluteUrl(customUrl) || customUrl;
  }

  const baseUrl = resolveFrontendBaseUrl();

  // Construct URL with host-based domain if type and slug are provided
  if (type && slug) {
    let path = '';

    switch (type) {
      case 'package':
        path = `/package-details/${slug}`;
        break;
      case 'activity':
        path = `/activity-details/${slug}`;
        break;
      case 'stay':
        path = `/stay-experience-details/${slug}`;
        break;
      case 'riders-corner':
        path = `/riders-corner/${slug}`;
        break;
      default:
        return window.location.href;
    }

    if (path) {
      return toAbsoluteUrl(path, baseUrl) || `${baseUrl}${path}`;
    }
  }

  // Fallback to current URL
  return window.location.href;
};

export function useShare(options: UseShareOptions = {}): UseShareReturn {
  const { type, slug, customUrl, useFullUrl, title, description } = options;
  const { createShortUrl, loading } = useTinyUrl();
  const [popupOpen, setPopupOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState('');

  const openSharePopup = useCallback(
    async (overrideUrl?: string) => {
      // Guard against SSR
      if (typeof window === 'undefined') return;

      // When useFullUrl (e.g. booking ID in URL), use current page URL and do not shorten
      const shareBaseUrl = resolveFrontendBaseUrl();
      const fullUrl = useFullUrl
        ? toAbsoluteUrl(
            `${window.location.pathname}${window.location.search}`,
            shareBaseUrl
          ) || window.location.href
        : undefined;
      const baseUrl =
        overrideUrl ??
        (useFullUrl ? fullUrl : constructShareUrl(type, slug, customUrl));

      const finalUrl = useFullUrl
        ? baseUrl
        : (await createShortUrl(baseUrl)) || baseUrl;
      setShareUrl(finalUrl);
      setPopupOpen(true);
    },
    [type, slug, customUrl, useFullUrl, createShortUrl]
  );

  const shareTitle = title || getShareTitle(type);
  const shareDescription = description || getShareDescription(type);

  return {
    openSharePopup,
    shareUrl,
    popupOpen,
    setPopupOpen,
    shareTitle,
    shareDescription,
    loading,
  };
}
